import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Title,
  Stack,
  Text,
  Button,
  Group,
  Badge,
  Alert,
  Modal,
  Select,
  Code,
  CopyButton,
  Tooltip,
} from '@mantine/core';
import { IconBrandTelegram, IconCheck, IconCopy, IconUnlink } from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import logger from '@ayocore/platform/common/helpers/logger';
import type { TelegramUserBinding } from '../../schemas/telegram-user-binding.schema';
import type { TelegramBot } from '../../schemas/telegram-bot.schema';
import {
  getMyBinding,
  generatePersonalLink,
  removeMyBinding,
  useTelegramBotRepository,
} from '../rpc';

/**
 * Карточка управления личной Telegram-привязкой пользователя.
 *
 * Отображает текущие привязки, позволяет сгенерировать ссылку для привязки
 * (deep link через бота) и отвязать существующие привязки.
 */
export function TelegramBindingCard() {
  const [bindings, setBindings] = useState<TelegramUserBinding[]>([]);
  const [activeBots, setActiveBots] = useState<TelegramBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Состояние модалки генерации ссылки
  const [linkModalOpened, setLinkModalOpened] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // Состояние удаления привязки
  const [removingId, setRemovingId] = useState<number | null>(null);

  const botRepo = useTelegramBotRepository();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bindingsResult, botsResult] = await Promise.all([getMyBinding(), botRepo.fetch()]);
      setBindings(bindingsResult);
      setActiveBots((botsResult ?? []).filter((b: TelegramBot) => b.is_active));
    } catch (err) {
      logger.error('Не удалось загрузить данные Telegram-привязки:', err);
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [botRepo]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleOpenModal = async () => {
    setGeneratedLink(null);
    setLinkError(null);
    setLinkModalOpened(true);

    if (activeBots.length === 1) {
      const botId = activeBots[0].id;
      setSelectedBotId(String(botId));
      setGeneratingLink(true);
      try {
        const result = await generatePersonalLink({ bot_id: botId });
        setGeneratedLink(result.link);
      } catch (err) {
        logger.error('Не удалось сгенерировать ссылку привязки:', err);
        setLinkError('Не удалось сгенерировать ссылку');
      } finally {
        setGeneratingLink(false);
      }
    } else {
      setSelectedBotId(null);
    }
  };

  const handleCloseModal = () => {
    setLinkModalOpened(false);
    // Если ссылка была сгенерирована — перезагружаем данные (вдруг пользователь уже привязался)
    if (generatedLink) void loadData();
  };

  const handleGenerateLink = async (botIdOverride?: number) => {
    const botId = botIdOverride ?? (selectedBotId ? parseInt(selectedBotId) : null);
    if (!botId) {
      setLinkError('Выберите бота');
      return;
    }
    setGeneratingLink(true);
    setLinkError(null);
    try {
      const result = await generatePersonalLink({ bot_id: botId });
      setGeneratedLink(result.link);
    } catch (err) {
      logger.error('Не удалось сгенерировать ссылку привязки:', err);
      setLinkError('Не удалось сгенерировать ссылку');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleRemoveBinding = async (bindingId: number) => {
    setRemovingId(bindingId);
    setError(null);
    try {
      await removeMyBinding({ binding_id: bindingId });
      await loadData();
    } catch (err) {
      logger.error('Не удалось отвязать Telegram-аккаунт:', err);
      setError('Не удалось отвязать аккаунт');
    } finally {
      setRemovingId(null);
    }
  };

  // Не показываем карточку пока грузится (чтобы не было скачка)
  if (loading) return null;

  return (
    <>
      <Card shadow='sm' padding='lg' radius='md' withBorder>
        <Title order={2} size='h3' mb='md'>
          <Group gap='xs'>
            <IconBrandTelegram size={20} />
            Telegram
          </Group>
        </Title>

        {error && (
          <Alert color='red' mb='md' withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {bindings.length === 0 ? (
          <Stack gap='sm'>
            <Text c='dimmed' size='sm'>
              Telegram-аккаунт не привязан
            </Text>
            <Button
              variant='light'
              leftSection={<IconBrandTelegram size={16} />}
              onClick={handleOpenModal}
              disabled={activeBots.length === 0}
              w='fit-content'
            >
              Привязать Telegram
            </Button>
            {activeBots.length === 0 && (
              <Text size='xs' c='dimmed'>
                Нет активных Telegram-ботов
              </Text>
            )}
          </Stack>
        ) : (
          <Stack gap='sm'>
            {bindings.map(binding => (
              <Group key={binding.id} justify='space-between' align='center'>
                <Group gap='xs'>
                  <Badge color='green' variant='light'>
                    Привязан
                  </Badge>
                  <Text size='sm'>
                    {binding.telegram_first_name ?? '—'}
                    {binding.telegram_username ? ` (@${binding.telegram_username})` : ''}
                  </Text>
                </Group>
                <Button
                  size='xs'
                  variant='subtle'
                  color='red'
                  leftSection={<IconUnlink size={14} />}
                  onClick={() => handleRemoveBinding(binding.id)}
                  loading={removingId === binding.id}
                >
                  Отвязать
                </Button>
              </Group>
            ))}
            {activeBots.length > 0 && (
              <Button
                variant='light'
                leftSection={<IconBrandTelegram size={16} />}
                onClick={handleOpenModal}
                w='fit-content'
                size='sm'
              >
                Привязать ещё
              </Button>
            )}
          </Stack>
        )}
      </Card>

      {/* Модалка генерации ссылки привязки */}
      <Modal
        opened={linkModalOpened}
        onClose={handleCloseModal}
        title='Привязать Telegram'
        size='md'
      >
        <Stack gap='md'>
          {activeBots.length > 1 && !generatedLink && (
            <Select
              label='Выберите бота'
              placeholder='Выберите бота'
              data={activeBots.map(b => ({
                value: String(b.id),
                label: `@${b.bot_username} — ${b.name}`,
              }))}
              value={selectedBotId}
              onChange={value => {
                setSelectedBotId(value);
                if (value) void handleGenerateLink(parseInt(value));
              }}
            />
          )}

          {linkError && (
            <Alert color='red' withCloseButton onClose={() => setLinkError(null)}>
              {linkError}
            </Alert>
          )}

          {generatingLink ? (
            <Stack gap='sm' align='center'>
              <Text size='sm' c='dimmed'>
                Генерация ссылки...
              </Text>
            </Stack>
          ) : generatedLink ? (
            <Stack gap='xs' align='center'>
              <QRCode value={generatedLink} size={180} />
              <Text size='sm' fw={500}>
                Или скопируйте ссылку:
              </Text>
              <Code block style={{ alignSelf: 'stretch' }}>
                {generatedLink}
              </Code>
              <CopyButton value={generatedLink}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Скопировано' : 'Копировать'} withArrow position='right'>
                    <Button
                      leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      onClick={copy}
                      variant='light'
                      fullWidth
                    >
                      {copied ? 'Скопировано' : 'Копировать ссылку'}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>
              <Text size='xs' c='dimmed'>
                Отсканируйте QR-код или перейдите по ссылке в Telegram.
              </Text>
            </Stack>
          ) : null}
        </Stack>
      </Modal>
    </>
  );
}
